import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Enhanced Register Component - Ultra Compact & Responsive
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Box,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Autocomplete,
  useTheme,
  Paper,
  useMediaQuery,
  Container,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Business,
  Person,
  Email,
  Lock,
  LocationOn,
  CheckCircle,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
  Done,
  ContentCopy
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { motion, AnimatePresence } from 'framer-motion';

import { useAuth } from '../../hooks/useAuth';
import { useApiCall } from '../../hooks/useApi';
import { api, handleApiError } from '../../services/api';
import { CURRENCIES } from '../../utils/constants';
import ArsenalLogo from '../common/ArsenalLogo';
import PhoneInput from '../common/PhoneInput';

// Hook para cargar datos geográficos (reutilizado de SettingsPage)
const useGeographicData = () => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [phoneCodes, setPhoneCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [countriesRes, statesRes, citiesRes, phoneCodesRes] = await Promise.all([
          fetch('/countries-master/json/countries.json').catch(() => ({ ok: false })),
          fetch('/countries-master/json/states.json').catch(() => ({ ok: false })),
          fetch('/countries-master/json/cities.json').catch(() => ({ ok: false })),
          fetch('/countries-master/json/codcountries.json').catch(() => ({ ok: false }))
        ]);

        let countriesData = [];
        let statesData = [];
        let citiesData = [];
        let phoneCodesData = [];

        if (countriesRes.ok) {
          const countriesJson = await countriesRes.json();
          countriesData = countriesJson.countries || [];
        }
        if (statesRes.ok) {
          const statesJson = await statesRes.json();
          statesData = statesJson.states || [];
        }
        if (citiesRes.ok) {
          const citiesJson = await citiesRes.json();
          citiesData = citiesJson.cities || [];
        }
        if (phoneCodesRes.ok) {
          phoneCodesData = await phoneCodesRes.json();
        }

        setCountries(countriesData);
        setStates(statesData);
        setCities(citiesData);
        setPhoneCodes(Array.isArray(phoneCodesData) ? phoneCodesData : []);
      } catch (error) {
        setError(error.message);
        setCountries([]);
        setStates([]);
        setCities([]);
        setPhoneCodes([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return { countries, states, cities, phoneCodes, loading, error };
};

// Componente compacto para el selector de país
const CountryAutocomplete = ({ value, onChange, countries, disabled = false }) => {
  const { t } = useTranslation();
  const selectedCountry = useMemo(() => {
    const safeCountries = Array.isArray(countries) ? countries : [];
    return safeCountries.find(country => country.name === value) || null;
  }, [value, countries]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled}
      value={selectedCountry}
      onChange={(event, newValue) => onChange(newValue ? newValue.name : '')}
      options={Array.isArray(countries) ? countries : []}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('settings.company.country')}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {option.name}
        </Box>
      )}
      noOptionsText={t('settings.company.noCountriesFound')}
    />
  );
};

// Componente compacto para el selector de estado
const StateAutocomplete = ({ value, onChange, selectedCountry, countries, states, disabled = false }) => {
  const { t } = useTranslation();
  const availableStates = useMemo(() => {
    const safeCountries = Array.isArray(countries) ? countries : [];
    const safeStates = Array.isArray(states) ? states : [];
    if (!selectedCountry) return [];
    const country = safeCountries.find(c => c.name === selectedCountry);
    if (!country) return [];
    return safeStates.filter(state => state.id_country === country.id);
  }, [selectedCountry, countries, states]);

  const selectedState = useMemo(() => {
    return availableStates.find(state => state.name === value) || null;
  }, [value, availableStates]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled || !selectedCountry}
      value={selectedState}
      onChange={(event, newValue) => onChange(newValue ? newValue.name : '')}
      options={availableStates}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('gps.table.status')}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {option.name}
        </Box>
      )}
      noOptionsText={selectedCountry ? t('hardcoded.components_auth_register.sin_estados') : t('hardcoded.components_auth_register.selecciona_pais')}
    />
  );
};

// Componente compacto para el selector de ciudad
const CityAutocomplete = ({ value, onChange, selectedState, states, cities, disabled = false }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const availableCities = useMemo(() => {
    const safeStates = Array.isArray(states) ? states : [];
    const safeCities = Array.isArray(cities) ? cities : [];
    if (!selectedState) return [];
    const state = safeStates.find(s => s.name === selectedState);
    if (!state) return [];

    let filteredCities = safeCities.filter(city => city.id_state === state.id);
    if (inputValue) {
      filteredCities = filteredCities.filter(city =>
        city.name.toLowerCase().includes(inputValue.toLowerCase())
      );
    }
    return filteredCities.slice(0, 50);
  }, [selectedState, states, cities, inputValue]);

  const selectedCity = useMemo(() => {
    return availableCities.find(city => city.name === value) || null;
  }, [value, availableCities]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled || !selectedState}
      value={selectedCity}
      onChange={(event, newValue) => onChange(newValue ? newValue.name : '')}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      options={availableCities}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('settings.company.city')}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {option.name}
        </Box>
      )}
      noOptionsText={selectedState ? t('hardcoded.components_auth_register.sin_ciudades') : t('hardcoded.components_auth_register.selecciona_estado')}
      filterOptions={(options) => options}
    />
  );
};

// Esquemas de validación
const getUserValidationSchema = (mode = 'new') => {
  const schema = {
    email: Yup.string().trim().email(i18n.t('components_auth_register.validation.invalid_email')).required(i18n.t('components_auth_register.validation.email_required')),
    phone: Yup.string().nullable(),
  };

  if (mode !== 'existing') {
    schema.name = Yup.string().min(2, i18n.t('components_auth_register.validation.min_chars')).required(i18n.t('components_auth_register.validation.name_required'));
    schema.last_name = Yup.string().min(2, i18n.t('components_auth_register.validation.min_chars')).required(i18n.t('components_auth_register.validation.last_name_required'));
    schema.password = Yup.string()
      .min(8, i18n.t('components_auth_register.validation.min_chars_password'))
      .matches(/[A-Z]/, i18n.t('components_auth_register.validation.one_uppercase'))
      .matches(/[a-z]/, i18n.t('components_auth_register.validation.one_lowercase'))
      .matches(/[0-9]/, i18n.t('components_auth_register.validation.one_number'))
      .matches(/[^A-Za-z0-9]/, i18n.t('components_auth_register.validation.one_symbol'))
      .required(i18n.t('components_auth_register.validation.password_required'));
    schema.confirmPassword = Yup.string()
      .oneOf([Yup.ref('password'), null], i18n.t('components_auth_register.validation.not_match'))
      .required(i18n.t('components_auth_register.validation.confirm_required'));
  }

  return Yup.object(schema);
};

const normalizeEmailValue = (value = '') => value.trim().toLowerCase();
const isValidEmailFormat = (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalizeEmailValue(value));
const getRenderableErrorMessage = (error, fallbackMessage) => {
  const apiError = handleApiError(error);
  return typeof apiError?.message === 'string' && apiError.message.trim()
    ? apiError.message
    : fallbackMessage;
};

const companyValidationSchema = Yup.object({
  company_name: Yup.string().min(2, i18n.t('hardcoded.components_auth_register.min_2_caracteres')).required(i18n.t('hardcoded.components_auth_register.empresa_requerida')),
  rfc: Yup.string().matches(/^[A-Z]{3,4}[0-9]{6}[A-Z0-9]{3}$/, i18n.t('hardcoded.components_auth_register.rfc_invalido')).required(i18n.t('hardcoded.components_auth_register.rfc_requerido')),
  manager_name: Yup.string().min(2, i18n.t('hardcoded.components_auth_register.min_2_caracteres')).required(i18n.t('hardcoded.components_auth_register.gerente_requerido')),
  manager_email: Yup.string().email(i18n.t('hardcoded.components_auth_register.email_invalido')).required(i18n.t('hardcoded.components_auth_register.email_requerido')),
  country: Yup.string().required(i18n.t('hardcoded.components_auth_register.pais_requerido')),
  currency: Yup.string().required(i18n.t('hardcoded.components_auth_register.moneda_requerida')),
});

const getPasswordRequirements = (password = '') => {
  const value = password || '';
  return [
    { label: i18n.t('hardcoded.components_auth_register.min_chars'), valid: value.length >= 8 },
    { label: i18n.t('hardcoded.components_auth_register.one_uppercase'), valid: /[A-Z]/.test(value) },
    { label: i18n.t('hardcoded.components_auth_register.one_lowercase'), valid: /[a-z]/.test(value) },
    { label: i18n.t('hardcoded.components_auth_register.one_number'), valid: /[0-9]/.test(value) },
    { label: i18n.t('hardcoded.components_auth_register.one_symbol'), valid: /[^A-Za-z0-9]/.test(value) },
  ];
};

const generateStrongPassword = (length = 12) => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  const all = upper + lower + numbers + symbols;

  const requiredChars = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ];

  const remaining = Array.from({ length: Math.max(length - requiredChars.length, 0) }, () => (
    all[Math.floor(Math.random() * all.length)]
  ));

  const mixed = [...requiredChars, ...remaining]
    .sort(() => Math.random() - 0.5)
    .join('');

  return mixed;
};

const Register = ({ onSuccess }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [searchParams] = useSearchParams();
  const { registerCompany, useInvitation: processInvitation } = useAuth();

  const inviteParam = searchParams.get('invite');
  const registrationToken = searchParams.get('reg');
  const [continueWithoutDefaultPlan, setContinueWithoutDefaultPlan] = useState(false);
  const effectiveRegistrationToken = continueWithoutDefaultPlan ? null : registrationToken;
  const isInviteMode = Boolean(inviteParam);
  const isRegistrationLinkMode = Boolean(effectiveRegistrationToken);

  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [registrationLinkData, setRegistrationLinkData] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [adminLookupStatus, setAdminLookupStatus] = useState('idle');
  const [existingAdminUser, setExistingAdminUser] = useState(null);
  const [adminLookupError, setAdminLookupError] = useState('');
  const [lastCheckedAdminEmail, setLastCheckedAdminEmail] = useState('');

  const { countries, states, cities, loading: geoLoading } = useGeographicData();
  const { data: servicePlans = [] } = useApiCall(isInviteMode ? null : api.auth.getServicePlans);
  const adminUserMode = isInviteMode
    ? 'new'
    : adminLookupStatus === 'existing'
      ? 'existing'
      : adminLookupStatus === 'new'
        ? 'new'
        : null;
  const userValidationSchema = useMemo(
    () => getUserValidationSchema(adminUserMode || 'new'),
    [adminUserMode]
  );

  useEffect(() => {
    let cancelled = false;

    const loadRegistrationLink = async () => {
      if (!effectiveRegistrationToken) return;
      try {
        const response = await api.auth.validateRegistrationLink(effectiveRegistrationToken);
        if (cancelled) return;

        const data = response.data;
        setRegistrationLinkData(data);
        if (data?.billing_plan?.id) {
          setSelectedPlan(data.billing_plan.id);
        }
      } catch (error) {
        if (!cancelled) {
          setRegistrationLinkData(null);
          setRegistrationError(getRenderableErrorMessage(error, t('hardcoded.components_auth_register.liga_de_registro_invalida_o')));
        }
      }
    };

    loadRegistrationLink();
    return () => {
      cancelled = true;
    };
  }, [effectiveRegistrationToken]);

  const userFormik = useFormik({
    initialValues: {
      name: '', last_name: '', email: '', password: '', confirmPassword: '', phone: '', language: 'es',
    },
    validationSchema: userValidationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (isInviteMode) {
        await handleInviteRegistration(values);
      } else {
        setActiveStep(2);
      }
    },
  });

  const companyFormik = useFormik({
    initialValues: {
      company_name: '', rfc: '', manager_name: '', manager_email: '',
      country: '', state: '', city: '', address: '', currency: 'MXN', phone: '',
    },
    validationSchema: companyValidationSchema,
    validateOnMount: true,
    onSubmit: () => setActiveStep(1),
  });

  const passwordRequirements = useMemo(
    () => getPasswordRequirements(userFormik.values.password),
    [userFormik.values.password]
  );
  const hasPreassignedPlan = Boolean(isRegistrationLinkMode && registrationLinkData?.billing_plan?.id);
  const showContinueWithoutPlan = Boolean(
    isRegistrationLinkMode
    && !registrationLinkData
    && registrationError
    && /(registration link not found|liga de registro inválida|expirada|invalid|not found)/i.test(registrationError)
  );
  const visiblePlans = useMemo(() => {
    if (!hasPreassignedPlan) return servicePlans;

    const preassignedPlanId = registrationLinkData?.billing_plan?.id;
    const matchedPlan = servicePlans.find((plan) => String(plan.id) === String(preassignedPlanId));

    if (matchedPlan) {
      return [matchedPlan];
    }

    return [{
      id: preassignedPlanId,
      plan_name: registrationLinkData?.billing_plan?.name || t('hardcoded.components_auth_register.plan_asignado'),
      cost: registrationLinkData?.end_customer_price ?? registrationLinkData?.integrator_price ?? 0,
      max_vehicles: null,
      description: registrationLinkData?.billing_plan?.description || '',
      currency: registrationLinkData?.billing_plan?.currency || registrationLinkData?.currency || 'USD',
    }];
  }, [hasPreassignedPlan, registrationLinkData, servicePlans]);
  const shouldShowNewAdminFields = adminUserMode === 'new';
  const canContinueAdminStep = adminUserMode === 'existing'
    ? isValidEmailFormat(userFormik.values.email) && Boolean(existingAdminUser)
    : adminUserMode === 'new'
      ? userFormik.isValid && adminLookupStatus === 'new'
      : false;

  const resetAdminLookup = useCallback(() => {
    setAdminLookupStatus('idle');
    setExistingAdminUser(null);
    setAdminLookupError('');
    setLastCheckedAdminEmail('');
  }, []);

  const lookupAdminUserByEmail = useCallback(async (rawEmail, { force = false } = {}) => {
    if (isInviteMode) return;

    const normalizedEmail = normalizeEmailValue(rawEmail);

    if (!normalizedEmail) {
      resetAdminLookup();
      return;
    }

    if (!isValidEmailFormat(normalizedEmail)) {
      setAdminLookupStatus('idle');
      setExistingAdminUser(null);
      setAdminLookupError('');
      setLastCheckedAdminEmail('');
      return;
    }

    if (!force && (adminLookupStatus === 'checking' || normalizedEmail === lastCheckedAdminEmail)) {
      return;
    }

    setAdminLookupStatus('checking');
    setExistingAdminUser(null);
    setAdminLookupError('');

    try {
      const response = await api.auth.validateRegistrationEmail({ email: normalizedEmail });
      const matchedUser = response.data?.user || null;

      setLastCheckedAdminEmail(normalizedEmail);

      if (response.data?.exists && matchedUser) {
        setExistingAdminUser(matchedUser);
        setAdminLookupStatus('existing');
      } else {
        setExistingAdminUser(null);
        setAdminLookupStatus('new');
      }
    } catch (error) {
      if (error?.response?.status === 422) {
        setExistingAdminUser(null);
        setAdminLookupStatus('idle');
        setAdminLookupError('');
        setLastCheckedAdminEmail(normalizedEmail);
        return;
      }

      setExistingAdminUser(null);
      setAdminLookupStatus('error');
      setAdminLookupError(getRenderableErrorMessage(error, t('hardcoded.components_auth_register.no_fue_posible_validar_el')));
      setLastCheckedAdminEmail('');
    }
  }, [adminLookupStatus, isInviteMode, lastCheckedAdminEmail, resetAdminLookup]);

  useEffect(() => {
    if (isInviteMode || activeStep !== 1) {
      return undefined;
    }

    const normalizedEmail = normalizeEmailValue(userFormik.values.email);
    if (!normalizedEmail) {
      if (adminLookupStatus !== 'idle') {
        resetAdminLookup();
      }
      return undefined;
    }

    if (!isValidEmailFormat(normalizedEmail)) {
      if (adminLookupStatus !== 'idle') {
        setAdminLookupStatus('idle');
        setExistingAdminUser(null);
        setAdminLookupError('');
        setLastCheckedAdminEmail('');
      }
      return undefined;
    }

    const timer = window.setTimeout(() => {
      lookupAdminUserByEmail(normalizedEmail);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [activeStep, adminLookupStatus, isInviteMode, lookupAdminUserByEmail, resetAdminLookup, userFormik.values.email]);

  const handleAdminEmailChange = (event) => {
    const nextValue = event.target.value;
    const normalizedEmail = normalizeEmailValue(nextValue);

    userFormik.setFieldValue('email', nextValue);

    if (!normalizedEmail) {
      resetAdminLookup();
      return;
    }

    if (normalizedEmail !== lastCheckedAdminEmail) {
      setAdminLookupStatus('idle');
      setExistingAdminUser(null);
      setAdminLookupError('');
      setLastCheckedAdminEmail('');
    }
  };

  const handleAdminEmailBlur = (event) => {
    userFormik.handleBlur(event);

    const normalizedEmail = normalizeEmailValue(event.target.value);
    if (normalizedEmail !== event.target.value) {
      userFormik.setFieldValue('email', normalizedEmail, false);
    }

    lookupAdminUserByEmail(normalizedEmail, { force: true });
  };

  const handleGeneratePassword = () => {
    const generatedPassword = generateStrongPassword();
    userFormik.setFieldValue(t('hardcoded.components_auth_createusermanually.password'), generatedPassword);
    userFormik.setFieldValue('confirmPassword', generatedPassword);
    userFormik.setFieldTouched(t('hardcoded.components_auth_createusermanually.password'), true, false);
    userFormik.setFieldTouched('confirmPassword', true, false);
    setCopiedPassword(false);
  };

  const handleCopyPassword = async () => {
    const passwordToCopy = userFormik.values.password || generateStrongPassword();

    if (!userFormik.values.password) {
      userFormik.setFieldValue(t('hardcoded.components_auth_createusermanually.password'), passwordToCopy);
      userFormik.setFieldValue('confirmPassword', passwordToCopy);
      userFormik.setFieldTouched(t('hardcoded.components_auth_createusermanually.password'), true, false);
      userFormik.setFieldTouched('confirmPassword', true, false);
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(passwordToCopy);
      } else {
        const tempInput = document.createElement(t('hardcoded.components_auth_register.textarea'));
        tempInput.value = passwordToCopy;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }

      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 1800);
    } catch (error) {
      setCopiedPassword(false);
    }
  };

  const handleContinueWithoutDefaultPlan = () => {
    setContinueWithoutDefaultPlan(true);
    setRegistrationError('');
    setRegistrationLinkData(null);
    setSelectedPlan('');
  };

  const handleInviteRegistration = async (userData) => {
    if (!inviteParam) {
      setRegistrationError(t('hardcoded.components_auth_register.no_se_encontro_codigo_de'));
      return;
    }

    setIsLoading(true);
    setRegistrationError('');

    try {
      const result = await processInvitation(inviteParam, { ...userData, phone: userData.phone || null });

      if (result && result.success) {
        onSuccess ? onSuccess() : navigate('/dashboard');
      } else {
        setRegistrationError(result?.message || t('hardcoded.components_auth_register.error_en_el_registro'));
      }
    } catch (error) {
      // El mensaje al usuario se queda generico a proposito, pero la causa real
      // NO puede desaparecer: sin esta traza, un fallo de JS previo a la peticion
      // (p. ej. un hook llamado fuera del render) era indistinguible de un error
      // del servidor y no dejaba ni rastro en los logs del backend.
      console.error('[registro] fallo no controlado al dar de alta:', error);
      setRegistrationError(t('hardcoded.components_auth_register.error_inesperado_intenta_de_nuevo'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyRegistration = async () => {
    setIsLoading(true);
    setRegistrationError('');

    try {
      const normalizedAdminEmail = normalizeEmailValue(userFormik.values.email);

      const companyData = {
        company_name: companyFormik.values.company_name,
        rfc: companyFormik.values.rfc.toUpperCase(),
        manager_name: companyFormik.values.manager_name,
        manager_email: normalizeEmailValue(companyFormik.values.manager_email),
        country: companyFormik.values.country,
        state: companyFormik.values.state || null,
        city: companyFormik.values.city || null,
        address: companyFormik.values.address || null,
        currency: companyFormik.values.currency,
        phone: companyFormik.values.phone || null,
      };

      const userData = adminUserMode === 'existing'
        ? {
          mode: 'existing',
          email: normalizedAdminEmail,
          existingUserId: existingAdminUser?.id,
          language: userFormik.values.language || 'es',
        }
        : {
          mode: 'new',
          name: userFormik.values.name,
          last_name: userFormik.values.last_name,
          email: normalizedAdminEmail,
          password: userFormik.values.password,
          phone: userFormik.values.phone || null,
          language: userFormik.values.language || 'es',
        };

      const result = await registerCompany(
        companyData,
        userData,
        null,
        selectedPlan,
        effectiveRegistrationToken
      );

      if (result.success) {
        if (result.requiresLogin) {
          navigate(`/login?email=${encodeURIComponent(normalizedAdminEmail)}&companyCreated=1`, { replace: true });
          return;
        }

        onSuccess ? onSuccess() : navigate('/dashboard');
      } else {
        setRegistrationError(result.message || t('hardcoded.components_auth_register.error_en_el_registro'));
      }
    } catch (error) {
      // El mensaje al usuario se queda generico a proposito, pero la causa real
      // NO puede desaparecer: sin esta traza, un fallo de JS previo a la peticion
      // (p. ej. un hook llamado fuera del render) era indistinguible de un error
      // del servidor y no dejaba ni rastro en los logs del backend.
      console.error('[registro] fallo no controlado al dar de alta:', error);
      setRegistrationError(t('hardcoded.components_auth_register.error_inesperado_intenta_de_nuevo'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyLocationChange = (field, value) => {
    companyFormik.setFieldValue(field, value);
    if (field === 'country') {
      companyFormik.setFieldValue(t('hardcoded.components_auth_register.state'), '');
      companyFormik.setFieldValue('city', '');
    } else if (field === 'state') {
      companyFormik.setFieldValue('city', '');
    }
  };

  const steps = isInviteMode ? [t('hardcoded.components_auth_register.crear_cuenta')] : [t('navbar.company'), t('inviteUser.roles.admin.label'), t('hardcoded.components_auth_register.plan')];

  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction < 0 ? 100 : -100, opacity: 0 })
  };

  if (geoLoading) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}25 100%)`
      }}>
        <Paper sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          <ArsenalLogo sx={{ width: 48, height: 48, color: theme.palette.primary.main, mb: 1 }} />
          <Typography variant="body2">{i18n.t('common.loading')}</Typography>
        </Paper>
      </Box>
    );
  }

  // Modo invitación - Diseño ultra compacto
  if (isInviteMode) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}25 100%)`,
        p: { xs: 1, sm: 2 },
      }}>
        <Container maxWidth="sm">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: theme.shadows[10] }}>
              {/* Header compacto */}
              <Box sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                p: { xs: 2, sm: 3 },
                textAlign: 'center'
              }}>
                <ArsenalLogo sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, color: 'white', mb: 1 }} />
                <Typography variant={isXs ? "h6" : "h5"} fontWeight={600} gutterBottom>
                  {t('hardcoded.components_auth_register.unete_al_equipo')}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {i18n.t('hardcoded.components_auth_register.arsenal_preventive_sensors')}</Typography>
              </Box>

              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {registrationError && (
                  <Alert severity={showContinueWithoutPlan ? 'warning' : 'error'} sx={{ mb: 2, py: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Typography variant="caption">{registrationError}</Typography>
                      {showContinueWithoutPlan && (
                        <Button size="small" variant="outlined" onClick={handleContinueWithoutDefaultPlan}>
                          {t('hardcoded.components_auth_register.continuar_sin_plan_predeterminado')}</Button>
                      )}
                    </Stack>
                  </Alert>
                )}

                <Box component="form" onSubmit={userFormik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="name"
                        label="Nombre"
                        value={userFormik.values.name}
                        onChange={userFormik.handleChange}
                        onBlur={userFormik.handleBlur}
                        error={userFormik.touched.name && Boolean(userFormik.errors.name)}
                        helperText={userFormik.touched.name && userFormik.errors.name}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Person fontSize="small" /></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="last_name"
                        label={t('createUser.form.lastName')}
                        value={userFormik.values.last_name}
                        onChange={userFormik.handleChange}
                        onBlur={userFormik.handleBlur}
                        error={userFormik.touched.last_name && Boolean(userFormik.errors.last_name)}
                        helperText={userFormik.touched.last_name && userFormik.errors.last_name}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        name="email"
                        label={t('createUser.form.email')}
                        type="email"
                        value={userFormik.values.email}
                        onChange={userFormik.handleChange}
                        onBlur={userFormik.handleBlur}
                        error={userFormik.touched.email && Boolean(userFormik.errors.email)}
                        helperText={userFormik.touched.email && userFormik.errors.email}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name={t('hardcoded.components_auth_createusermanually.password')}
                        label={t('createUser.sections.password')}
                        type={showPassword ? 'text' : 'password'}
                        value={userFormik.values.password}
                        onChange={userFormik.handleChange}
                        onBlur={userFormik.handleBlur}
                        error={userFormik.touched.password && Boolean(userFormik.errors.password)}
                        helperText={userFormik.touched.password && userFormik.errors.password}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment>,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="confirmPassword"
                        label={t('createUser.form.confirmPassword')}
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={userFormik.values.confirmPassword}
                        onChange={userFormik.handleChange}
                        onBlur={userFormik.handleBlur}
                        error={userFormik.touched.confirmPassword && Boolean(userFormik.errors.confirmPassword)}
                        helperText={userFormik.touched.confirmPassword && userFormik.errors.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Stack spacing={0.5}>
                          {passwordRequirements.map((item) => (
                            <Stack key={item.label} direction="row" spacing={0.5} alignItems="center">
                              <CheckCircle color={item.valid ? 'success' : 'disabled'} sx={{ fontSize: 14 }} />
                              <Typography variant="caption" color={item.valid ? 'success.main' : 'text.secondary'}>
                                {item.label}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                        <Button size="small" variant="outlined" onClick={handleGeneratePassword}>
                          {t('hardcoded.components_auth_register.generar_contrasena')}</Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopy fontSize="small" />}
                          onClick={handleCopyPassword}
                        >
                          {copiedPassword ? t('hardcoded.components_auth_register.copiada') : t('hardcoded.components_auth_register.copiar_contrasena')}
                        </Button>
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <PhoneInput
                        value={userFormik.values.phone}
                        onChange={(value) => userFormik.setFieldValue('phone', value)}
                        label={t('settings.company.phone')}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isLoading || !userFormik.isValid}
                    sx={{
                      mt: 3,
                      py: 1.2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      fontWeight: 600,
                    }}
                  >
                    {isLoading ? t('createUser.form.creating') : t('hardcoded.components_auth_register.crear_cuenta')}
                  </Button>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('hardcoded.components_auth_register.ya_tienes_cuenta')}{' '}
                    <Link
                      component={RouterLink}
                      to={`/login?invite=${inviteParam}`}
                      sx={{ color: 'primary.main', fontWeight: 600 }}
                    >
                      {t('hardcoded.components_auth_register.iniciar_sesion')}</Link>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // Modo empresa - Diseño ultra compacto sin scroll
  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}25 100%)`,
      p: { xs: 0.5, sm: 1 },
    }}>
      <Container maxWidth={isLg ? "lg" : "md"} sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          style={{ width: '100%' }}
        >
          <Card sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: theme.shadows[12],
            height: { xs: '100vh', sm: 'auto' },
            maxHeight: { sm: '95vh' },
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header ultra compacto */}
            <Box sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              p: { xs: 1.5, sm: 2 },
              textAlign: 'center',
              flexShrink: 0
            }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <ArsenalLogo sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, color: 'white' }} />
                <Box>
                  <Typography variant={isXs ? "h6" : "h5"} fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {t('hardcoded.components_auth_register.crear_cuenta')}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {i18n.t('hardcoded.components_auth_register.arsenal_preventive_sensors')}</Typography>
                </Box>
              </Stack>
            </Box>

            {/* Stepper compacto */}
            <Box sx={{ px: { xs: 1, sm: 2 }, py: 1, flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel={isXs}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 600
                  },
                  '& .MuiStepIcon-root': {
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                  }
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Contenido principal */}
            <CardContent sx={{
              p: { xs: 1, sm: 2 },
              flex: 1,
              overflow: 'auto',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: theme.palette.divider, borderRadius: '4px' }
            }}>
              {registrationError && (
                <Alert severity={showContinueWithoutPlan ? 'warning' : 'error'} sx={{ mb: 2, py: 1 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Typography variant="caption">{registrationError}</Typography>
                    {showContinueWithoutPlan && (
                      <Button size="small" variant="outlined" onClick={handleContinueWithoutDefaultPlan}>
                        {t('hardcoded.components_auth_register.continuar_sin_plan_predeterminado')}</Button>
                    )}
                  </Stack>
                </Alert>
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3 }}
                >
                  {/* Paso 1: Empresa */}
                  {activeStep === 0 && (
                    <Box>
                      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            name="company_name"
                            label={t('navbar.company')}
                            value={companyFormik.values.company_name}
                            onChange={companyFormik.handleChange}
                            onBlur={companyFormik.handleBlur}
                            error={companyFormik.touched.company_name && Boolean(companyFormik.errors.company_name)}
                            helperText={companyFormik.touched.company_name && companyFormik.errors.company_name}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Business fontSize="small" /></InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            name="rfc"
                            label="RFC"
                            value={companyFormik.values.rfc}
                            onChange={(e) => companyFormik.setFieldValue('rfc', e.target.value.toUpperCase())}
                            onBlur={companyFormik.handleBlur}
                            error={companyFormik.touched.rfc && Boolean(companyFormik.errors.rfc)}
                            helperText={companyFormik.touched.rfc && companyFormik.errors.rfc}
                            placeholder="ABC123456XYZ"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            name="manager_name"
                            label={t('inviteUser.roles.manager.label')}
                            value={companyFormik.values.manager_name}
                            onChange={companyFormik.handleChange}
                            onBlur={companyFormik.handleBlur}
                            error={companyFormik.touched.manager_name && Boolean(companyFormik.errors.manager_name)}
                            helperText={companyFormik.touched.manager_name && companyFormik.errors.manager_name}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Person fontSize="small" /></InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            name="manager_email"
                            label={t('hardcoded.components_auth_register.email_gerente')}
                            type="email"
                            value={companyFormik.values.manager_email}
                            onChange={companyFormik.handleChange}
                            onBlur={companyFormik.handleBlur}
                            error={companyFormik.touched.manager_email && Boolean(companyFormik.errors.manager_email)}
                            helperText={companyFormik.touched.manager_email && companyFormik.errors.manager_email}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <PhoneInput
                            value={companyFormik.values.phone}
                            onChange={(value) => companyFormik.setFieldValue('phone', value)}
                            label={t('hardcoded.components_auth_register.telefono_de_la_empresa')}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <CountryAutocomplete
                            value={companyFormik.values.country}
                            onChange={(value) => handleCompanyLocationChange('country', value)}
                            countries={countries}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <StateAutocomplete
                            value={companyFormik.values.state}
                            onChange={(value) => handleCompanyLocationChange('state', value)}
                            selectedCountry={companyFormik.values.country}
                            countries={countries}
                            states={states}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <CityAutocomplete
                            value={companyFormik.values.city}
                            onChange={(value) => handleCompanyLocationChange('city', value)}
                            selectedState={companyFormik.values.state}
                            states={states}
                            cities={cities}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            size="small"
                            name={t('hardcoded.components_auth_register.address')}
                            label={t('hardcoded.components_auth_register.direccion_opcional')}
                            value={companyFormik.values.address}
                            onChange={companyFormik.handleChange}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><LocationOn fontSize="small" /></InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Autocomplete
                            fullWidth
                            size="small"
                            value={CURRENCIES.find(c => c.code === companyFormik.values.currency) || null}
                            onChange={(event, newValue) => companyFormik.setFieldValue('currency', newValue ? newValue.code : '')}
                            options={CURRENCIES}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('settings.company.currency')}
                                size="small"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Paso 2: Usuario */}
                  {activeStep === 1 && (
                    <Box>
                      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            name="email"
                            label={t('hardcoded.components_auth_register.email_del_administrador')}
                            type="email"
                            value={userFormik.values.email}
                            onChange={handleAdminEmailChange}
                            onBlur={handleAdminEmailBlur}
                            error={userFormik.touched.email && Boolean(userFormik.errors.email)}
                            helperText={
                              adminLookupStatus === 'idle'
                                ? (userFormik.touched.email && userFormik.errors.email)
                                : undefined
                            }
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment>,
                              endAdornment: adminLookupStatus === 'checking' ? (
                                <InputAdornment position="end">
                                  <CircularProgress size={16} />
                                </InputAdornment>
                              ) : undefined,
                            }}
                          />
                        </Grid>
                        {adminLookupStatus === 'checking' && (
                          <Grid item xs={12}>
                            <Alert severity="info" sx={{ py: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={16} />
                                <Typography variant="body2">{t('hardcoded.components_auth_register.buscando_usuario')}</Typography>
                              </Stack>
                            </Alert>
                          </Grid>
                        )}

                        {adminLookupStatus === 'existing' && existingAdminUser && (
                          <>
                            <Grid item xs={12}>
                              <Alert severity="success" sx={{ py: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {t('hardcoded.components_auth_register.usuario_existente_encontrado')}</Typography>
                                <Typography variant="caption">
                                  {t('hardcoded.components_auth_register.este_usuario_sera_agregado_como')}</Typography>
                              </Alert>
                            </Grid>
                            <Grid item xs={12}>
                              <Paper variant="outlined" sx={{ p: 2, borderColor: 'success.main', bgcolor: 'success.main', color: 'success.contrastText' }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                      {existingAdminUser.fullName || [existingAdminUser.firstName, existingAdminUser.lastName].filter(Boolean).join(' ')}
                                    </Typography>
                                    <Typography variant="body2">
                                      {existingAdminUser.email}
                                    </Typography>
                                  </Box>
                                  <Chip color="success" size="small" label={t('hardcoded.components_auth_register.usuario_existente')} />
                                </Stack>
                              </Paper>
                            </Grid>
                          </>
                        )}

                        {adminLookupStatus === 'new' && (
                          <Grid item xs={12}>
                            <Alert severity="info" sx={{ py: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {t('hardcoded.components_auth_register.no_encontramos_un_usuario_con')}</Typography>
                              <Typography variant="caption">
                                {t('hardcoded.components_auth_register.completa_los_datos_para_crearlo')}</Typography>
                            </Alert>
                          </Grid>
                        )}

                        {adminLookupStatus === 'error' && adminLookupError && (
                          <Grid item xs={12}>
                            <Alert severity="warning" sx={{ py: 1 }}>
                              <Typography variant="caption">{adminLookupError}</Typography>
                            </Alert>
                          </Grid>
                        )}

                        <AnimatePresence initial={false}>
                          {shouldShowNewAdminFields && (
                            <Grid item xs={12}>
                              <motion.div
                                key="new-admin-fields"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      name="name"
label={t('createUser.form.firstName')}
                                      value={userFormik.values.name}
                                      onChange={userFormik.handleChange}
                                      onBlur={userFormik.handleBlur}
                                      error={userFormik.touched.name && Boolean(userFormik.errors.name)}
                                      helperText={userFormik.touched.name && userFormik.errors.name}
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start"><Person fontSize="small" /></InputAdornment>,
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      name="last_name"
                                      label={t('createUser.form.lastName')}
                                      value={userFormik.values.last_name}
                                      onChange={userFormik.handleChange}
                                      onBlur={userFormik.handleBlur}
                                      error={userFormik.touched.last_name && Boolean(userFormik.errors.last_name)}
                                      helperText={userFormik.touched.last_name && userFormik.errors.last_name}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      name={t('hardcoded.components_auth_createusermanually.password')}
                                      label={t('createUser.sections.password')}
                                      type={showPassword ? 'text' : 'password'}
                                      value={userFormik.values.password}
                                      onChange={userFormik.handleChange}
                                      onBlur={userFormik.handleBlur}
                                      error={userFormik.touched.password && Boolean(userFormik.errors.password)}
                                      helperText={userFormik.touched.password && userFormik.errors.password}
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment>,
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      name="confirmPassword"
                                      label={t('createUser.form.confirmPassword')}
                                      type={showConfirmPassword ? 'text' : 'password'}
                                      value={userFormik.values.confirmPassword}
                                      onChange={userFormik.handleChange}
                                      onBlur={userFormik.handleBlur}
                                      error={userFormik.touched.confirmPassword && Boolean(userFormik.errors.confirmPassword)}
                                      helperText={userFormik.touched.confirmPassword && userFormik.errors.confirmPassword}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                              {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                      <Stack spacing={0.5}>
                                        {passwordRequirements.map((item) => (
                                          <Stack key={item.label} direction="row" spacing={0.5} alignItems="center">
                                            <CheckCircle color={item.valid ? 'success' : 'disabled'} sx={{ fontSize: 14 }} />
                                            <Typography variant="caption" color={item.valid ? 'success.main' : 'text.secondary'}>
                                              {item.label}
                                            </Typography>
                                          </Stack>
                                        ))}
                                      </Stack>
                                      <Button size="small" variant="outlined" onClick={handleGeneratePassword}>
                                        {t('hardcoded.components_auth_register.generar_contrasena')}</Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ContentCopy fontSize="small" />}
                                        onClick={handleCopyPassword}
                                      >
                                        {copiedPassword ? t('hardcoded.components_auth_register.copiada') : t('hardcoded.components_auth_register.copiar_contrasena')}
                                      </Button>
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <PhoneInput
                                      value={userFormik.values.phone}
                                      onChange={(value) => userFormik.setFieldValue('phone', value)}
                                      label={t('settings.company.phone')}
                                    />
                                  </Grid>
                                </Grid>
                              </motion.div>
                            </Grid>
                          )}
                        </AnimatePresence>
                      </Grid>
                    </Box>
                  )}

                  {/* Paso 3: Planes */}
                  {activeStep === 2 && (
                    <Box>
                      <Grid container spacing={2}>
                        {visiblePlans.map((plan) => (
                          <Grid item xs={12} sm={6} key={plan.id}>
                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                              <Card
                                variant="outlined"
                                sx={{
                                  cursor: 'pointer',
                                  borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                                  borderWidth: selectedPlan === plan.id ? 2 : 1,
                                  background: selectedPlan === plan.id ? 'primary.main' : 'background.paper',
                                  color: selectedPlan === plan.id ? 'primary.contrastText' : 'text.primary',
                                  transition: 'all 0.2s',
                                  minHeight: { xs: 120, sm: 140 },
                                  '&:hover': { borderColor: 'primary.main', boxShadow: 4 },
                                }}
                                onClick={() => {
                                  if (!hasPreassignedPlan) {
                                    setSelectedPlan(plan.id);
                                  }
                                }}
                              >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {plan.plan_name}
                                    </Typography>
                                    {selectedPlan === plan.id && <Done fontSize="small" />}
                                  </Stack>
                                  <Typography variant="h6" fontWeight={700} color="inherit">
                                    ${plan.cost}
                                    <Typography component="span" variant="caption" sx={{ opacity: 0.8 }}>
                                      /mes
                                    </Typography>
                                  </Typography>
                                  <Stack spacing={0.5} mt={1}>
                                    {plan.max_vehicles && (
                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                        <CheckCircle sx={{ fontSize: 14, opacity: 0.9 }} />
                                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                          {t('audit.filters.to')}{plan.max_vehicles} {t('vehicles.title')}</Typography>
                                      </Stack>
                                    )}
                                    {plan.description && (
                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                        <CheckCircle sx={{ fontSize: 14, opacity: 0.9 }} />
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                          {plan.description}
                                        </Typography>
                                      </Stack>
                                    )}
                                  </Stack>
                                </CardContent>
                              </Card>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>
                      {isRegistrationLinkMode && registrationLinkData && (
                        <Alert severity="info" sx={{ mt: 2, py: 1 }}>
                          <Typography variant="caption">
                            {t('hardcoded.components_auth_register.esta_liga_tiene_plan_preasignado')}{registrationLinkData.billing_plan?.name}
                          </Typography>
                        </Alert>
                      )}
                      {!selectedPlan && !hasPreassignedPlan && (
                        <Alert severity="info" sx={{ mt: 2, py: 1 }}>
                          <Typography variant="caption">{t('hardcoded.components_auth_register.selecciona_un_plan_para_continuar')}</Typography>
                        </Alert>
                      )}
                    </Box>
                  )}
                </motion.div>
              </AnimatePresence>
            </CardContent>

            {/* Footer con botones */}
            <Box sx={{
              p: { xs: 1, sm: 2 },
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              background: 'background.default'
            }}>
              {activeStep === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('hardcoded.components_auth_register.ya_tienes_cuenta')}{' '}
                    <Link component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
                      {t('hardcoded.components_auth_register.iniciar_sesion')}</Link>
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={companyFormik.handleSubmit}
                    disabled={!companyFormik.isValid}
                    endIcon={<ChevronRight fontSize="small" />}
                    sx={{ fontWeight: 600 }}
                  >
                    {i18n.t('hardcoded.components_auth_register.siguiente')}</Button>
                </Box>
              ) : activeStep === 1 ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Button
                    size="small"
                    onClick={() => setActiveStep(0)}
                    startIcon={<ChevronLeft fontSize="small" />}
                  >
                    {t('hardcoded.components_auth_joincompany.atras')}</Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={userFormik.handleSubmit}
                    disabled={adminLookupStatus === 'checking' || !canContinueAdminStep}
                    endIcon={<ChevronRight fontSize="small" />}
                    sx={{ fontWeight: 600 }}
                  >
                    {adminLookupStatus === 'checking' ? t('hardcoded.components_auth_register.validando') : t('hardcoded.components_auth_register.siguiente')}
                  </Button>
                </Stack>
              ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Button
                    size="small"
                    onClick={() => setActiveStep(1)}
                    startIcon={<ChevronLeft fontSize="small" />}
                  >
                    {t('hardcoded.components_auth_joincompany.atras')}</Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCompanyRegistration}
                    disabled={isLoading || !selectedPlan}
                    endIcon={<Done fontSize="small" />}
                    sx={{
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    }}
                  >
                    {isLoading ? t('createUser.form.creating') : t('hardcoded.components_auth_register.crear_cuenta')}
                  </Button>
                </Stack>
              )}
            </Box>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Register;